import { useEffect, useRef, useCallback } from "react"

type IntersectionObserverOptions = {
    root?: Element | null
    rootMargin?: string
    threshold?: number | number[]
  }
  
  export const useImageIntersectionObserver = (
    callback: (isIntersecting: boolean) => void,
    options?: IntersectionObserverOptions
  ) => {
    const targetRef = useRef<Element | null>(null)
    const observer = useRef<IntersectionObserver | null>(null)
  
    const handleIntersect = useCallback(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          callback(entry.isIntersecting)
        })
      },
      [callback]
    )
  
    useEffect(() => {
      const target = targetRef.current
      if (!target) return
  
      // Ensure previous observer is disconnected
      if (observer.current) {
        observer.current.disconnect()
      }
  
      observer.current = new IntersectionObserver(handleIntersect, options)
      observer.current.observe(target)
  
      // Cleanup function
      return () => {
        if (observer.current) {
          observer.current.disconnect()
          observer.current = null // Explicitly nullify on cleanup
        }
      }
    }, [options, handleIntersect]) // Re-run effect if options or callback change
  
    return targetRef // Return ref for the component to attach
  }